import React from 'react';
import { Box, Typography, Container, Link } from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Privacy Policy</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
        Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This Privacy Policy describes how Talktokam ("we", "us", "our") collects, uses, and protects your personal
        information when you use our WhatsApp Business API platform and related services ("Service").
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>1. Information We Collect</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Account Information:</strong> When you register, we collect your name, email address, phone number,
        business name, business category, website URL, Facebook Business Manager ID, and WhatsApp Business Number.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Business Details:</strong> Company address, city, state, country, industry category, and payment
        information (processed securely through Razorpay).
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Customer Contacts:</strong> Phone numbers, names, email addresses, and other contact details of your
        customers that you import or that are captured through your chatbot conversations.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Message Content:</strong> Messages sent and received through the Service, including text, media files,
        and conversation history.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>Usage Data:</strong> Log data, IP addresses, browser type, device information, and usage patterns
        within the Service.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>2. How We Use Your Information</Typography>
      <Box component="ul" sx={{ mb: 2, pl: 3 }}>
        <li><Typography variant="body2" color="text.secondary">To provide, operate, and maintain the Service</Typography></li>
        <li><Typography variant="body2" color="text.secondary">To process your transactions and send related information</Typography></li>
        <li><Typography variant="body2" color="text.secondary">To send administrative notifications (service updates, security alerts, support messages)</Typography></li>
        <li><Typography variant="body2" color="text.secondary">To monitor and analyze usage patterns to improve the Service</Typography></li>
        <li><Typography variant="body2" color="text.secondary">To detect, prevent, and address technical issues and security threats</Typography></li>
        <li><Typography variant="body2" color="text.secondary">To comply with legal obligations and enforce our terms</Typography></li>
      </Box>

      <Typography variant="h6" fontWeight={700} gutterBottom>3. Data Sharing</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        We may share your information with:
      </Typography>
      <Box component="ul" sx={{ mb: 2, pl: 3 }}>
        <li><Typography variant="body2" color="text.secondary"><strong>Meta/WhatsApp:</strong> As necessary to deliver messages through the WhatsApp Business Platform, subject to Meta's Terms of Service and Privacy Policy</Typography></li>
        <li><Typography variant="body2" color="text.secondary"><strong>Service Providers:</strong> Third-party vendors who assist in operating our platform (hosting, payment processing, analytics)</Typography></li>
        <li><Typography variant="body2" color="text.secondary"><strong>Legal Requirements:</strong> When required by law, court order, or governmental regulation</Typography></li>
        <li><Typography variant="body2" color="text.secondary"><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with appropriate notice</Typography></li>
      </Box>

      <Typography variant="h6" fontWeight={700} gutterBottom>4. Data Retention</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We retain your account information for as long as your account is active. Conversation data and contact
        information are retained as long as you use the Service. After account deletion, we may retain certain
        information as required by law or for legitimate business purposes, but will remove personally identifiable
        information where possible.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>5. Data Security</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We implement industry-standard security measures including encryption in transit (TLS), secure authentication
        (JWT tokens), encrypted storage, and regular security audits. However, no method of transmission over the
        Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>6. International Data Transfers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your data may be processed in countries other than your own, including the United States, where our servers
        and third-party service providers are located. By using the Service, you consent to the transfer of your
        information to these countries, which may have different data protection laws than your jurisdiction.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>7. Your Rights</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        You have the right to:
      </Typography>
      <Box component="ul" sx={{ mb: 2, pl: 3 }}>
        <li><Typography variant="body2" color="text.secondary">Access the personal information we hold about you</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Request correction of inaccurate data</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Request deletion of your personal data</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Object to or restrict the processing of your data</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Export your data in a portable format</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Withdraw consent at any time (where processing is based on consent)</Typography></li>
      </Box>

      <Typography variant="h6" fontWeight={700} gutterBottom>8. Customer Contact Data</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When you use the Service to communicate with your customers, you are the data controller for your customers'
        personal information. You are responsible for obtaining proper consent from your customers, handling opt-out
        requests, and complying with applicable data protection laws (including GDPR where applicable). We act as a
        data processor on your behalf.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>9. Cookies & Tracking</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We use essential cookies to maintain your session and authentication. We do not use third-party advertising
        cookies or tracking pixels. Analytics data is collected in aggregate form to improve the Service.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>10. Children's Privacy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The Service is not intended for use by individuals under the age of 18. We do not knowingly collect personal
        information from children. If we become aware that we have collected personal information from a child, we
        will take steps to delete such information promptly.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>11. Changes to This Policy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We may update this Privacy Policy from time to time. We will notify you of any material changes by posting
        the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the Service
        after any changes constitutes acceptance of the updated policy.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>12. Contact Us</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact us at{' '}
        <Link href="mailto:support@talktokam.com" sx={{ color: '#075e54' }}>support@talktokam.com</Link>
      </Typography>
    </Container>
  );
};

export default PrivacyPolicy;
